import React from "react";
import { userDetail } from "Stores/userDetail";

// A schedule runs every day (DAILY), Monday–Friday (WEEKDAYS), or on chosen days (WEEKLY + days,
// e.g. ["MONDAY", "WEDNESDAY", "THURSDAY"]). The scheduler reads trigger.config.frequency / days;
// older weekly agents carry a single dayOfWeek instead. Shared by every screen with a schedule.
export const WEEKDAYS = [
  { value: "MONDAY", label: "Monday", short: "Mon" },
  { value: "TUESDAY", label: "Tuesday", short: "Tue" },
  { value: "WEDNESDAY", label: "Wednesday", short: "Wed" },
  { value: "THURSDAY", label: "Thursday", short: "Thu" },
  { value: "FRIDAY", label: "Friday", short: "Fri" },
  { value: "SATURDAY", label: "Saturday", short: "Sat" },
  { value: "SUNDAY", label: "Sunday", short: "Sun" }
];

export const DEFAULT_DAYS = ["MONDAY"];

// Options for a schedule's "Run" select (screens that also allow manual runs add their own).
export const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Every day" },
  { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
  { value: "WEEKLY", label: "Specific days" }
];

const ORDER = WEEKDAYS.map(d => d.value);
const sortDays = days => ORDER.filter(d => days.includes(d));

// The chosen days of a schedule config (legacy single dayOfWeek included).
export const daysOf = config => {
  const c = config || {};
  const raw = Array.isArray(c.days) && c.days.length ? c.days : (c.dayOfWeek ? [c.dayOfWeek] : DEFAULT_DAYS);
  const days = sortDays(raw.map(d => String(d).toUpperCase()));
  return days.length ? days : DEFAULT_DAYS;
};

// "daily at 08:00" / "on weekdays at 08:00" / "every Monday at 08:00" / "every Mon, Wed & Thu at 08:00"
// The time zone a new agent's schedule runs in: the one set on the user's profile, else the
// browser's own (e.g. "Asia/Kolkata"), else UTC. A named zone, so daylight saving is followed.
export const defaultTimezone = () => {
  try {
    return userDetail.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return userDetail.timeZone || "UTC";
  }
};

// "GMT+5:30", "UTC", "EDT" — how a schedule's time zone is shown next to its time.
export const tzLabel = tz => {
  if (!tz) return "";
  if (/^(UTC|GMT|Etc\/UTC)$/i.test(tz)) return "UTC";
  try {
    const name = loc => {
      const part = new Intl.DateTimeFormat(loc, { timeZone: tz, timeZoneName: "short" })
        .formatToParts(new Date()).find(p => p.type === "timeZoneName");
      return part ? part.value : "";
    };
    // en-US only abbreviates US zones; the other English locales know their own and get summer
    // time right (en-GB: CET/CEST, BST; en-IN: IST; en-AU: AEST/AEDT). Else the offset, "GMT+9".
    let offset = "";
    for (const loc of ["en-US", "en-GB", "en-IN", "en-AU"]) {
      const short = name(loc);
      if (short && !/^GMT[+-]/.test(short)) return short;
      offset = offset || short;
    }
    return offset || tz;
  } catch (e) {
    return tz; // not a zone the browser knows — show it as stored
  }
};

// The timezone-list lookup is shared with Settings → Schedules.
export { findZoneOption } from "Utils/timezones";

export const scheduleText = (frequency, time, days, tz) => {
  const at = time ? ` at ${time}${tz ? ` ${tzLabel(tz)}` : ""}` : "";
  const f = String(frequency || "DAILY").toUpperCase();
  if (f === "WEEKDAYS") return `on weekdays${at}`;
  if (f !== "WEEKLY") return `daily${at}`;
  const chosen = daysOf({ days: Array.isArray(days) ? days : days ? [days] : [] });
  if (chosen.length === 7) return `daily${at}`;
  if (chosen.length === 5 && !chosen.includes("SATURDAY") && !chosen.includes("SUNDAY")) return `on weekdays${at}`;
  if (chosen.length === 1) return `every ${WEEKDAYS.find(d => d.value === chosen[0]).label}${at}`;
  const shorts = chosen.map(v => WEEKDAYS.find(d => d.value === v).short);
  return `every ${shorts.slice(0, -1).join(", ")} & ${shorts[shorts.length - 1]}${at}`;
};

// Pick one or more days; at least one always stays selected.
const DayOfWeekField = ({ value, onChange, label = "On" }) => {
  const selected = Array.isArray(value) && value.length ? value : DEFAULT_DAYS;
  const toggle = day => {
    const next = selected.includes(day) ? selected.filter(d => d !== day) : [...selected, day];
    if (next.length) onChange(sortDays(next));
  };
  return (
    <div className="ag-field ag-field--full">
      <span className="ag-field__label">{label}</span>
      <div className="ag-days">
        {WEEKDAYS.map(d => (
          <button
            key={d.value}
            type="button"
            title={d.label}
            aria-pressed={selected.includes(d.value)}
            className={`ag-day ${selected.includes(d.value) ? "is-on" : ""}`}
            onClick={() => toggle(d.value)}
          >
            {d.short}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DayOfWeekField;
